import {
  RBPayrollDocument,
  RBPayrollPaymentMethod,
  RBPayrollSetupState,
  RBPayrollStatus,
  RBPayrollStatusValue,
} from "../types/rbPayrollOwner.types";
import { normalizeRbPayStatus, RBPayStatus } from "../../../util/rbPay";

// Backend RB Pay payment-type value for RB Pay (owner location profile).
export const LOCATION_RB_PAY_PAYMENT_TYPE = "rb_pay";

// Map the canonical RB Pay status (util/rbPay) to the owner-settings status enum.
// Canonical is the single source of truth; this adapter keeps the owner UI's
// existing UPPERCASE enum (labels / copy / tones) working unchanged.
const OWNER_STATUS_FROM_CANONICAL: Record<string, RBPayrollStatusValue> = {
  [RBPayStatus.ACTIVE]: RBPayrollStatus.ACTIVE,
  [RBPayStatus.PENDING]: RBPayrollStatus.PENDING_REVIEW,
  [RBPayStatus.ON_HOLD]: RBPayrollStatus.ON_HOLD,
  [RBPayStatus.NOT_SETUP]: RBPayrollStatus.NOT_STARTED,
};

// Resolve the owner-settings status for a returned location profile. Prefers the
// backend `rbPayStatus` (new source of truth); falls back to the legacy
// `rbPayApproved` boolean while the field rolls out. A returned profile that
// carries neither a status nor approval reads as Pending Review (a profile
// exists, just not yet approved) — matching the prior behaviour.
export const resolveOwnerProfileStatus = (profile: any): RBPayrollStatusValue => {
  const raw = profile?.rbPayStatus ?? profile?.rb_pay_status;
  if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
    return OWNER_STATUS_FROM_CANONICAL[normalizeRbPayStatus(raw)] || RBPayrollStatus.PENDING_REVIEW;
  }
  return profile?.rbPayApproved ? RBPayrollStatus.ACTIVE : RBPayrollStatus.PENDING_REVIEW;
};

// `voidChequeS3Key` arrives as a presigned URL on GET (used to preview/download)
// or as a raw object key on write. Keep both: `previewUrl` for viewing, `s3Key`
// to re-send unchanged on update.
export const buildVoidChequeDocument = (value?: string | null): RBPayrollDocument | null => {
  if (!value) {
    return null;
  }

  let s3Key = value;
  let previewUrl = "";
  let name = "void-cheque";

  if (/^https?:\/\//i.test(value)) {
    previewUrl = value;
    try {
      const parsed = new URL(value);
      s3Key = parsed.pathname.replace(/^\//, "");
      name = decodeURIComponent(s3Key.split("/").pop() || name);
    } catch (error) {
      s3Key = value;
    }
  } else {
    name = value.split("/").pop() || name;
  }

  return {
    id: s3Key,
    name,
    type: /\.pdf(\?|$)/i.test(value) ? "application/pdf" : "image/jpeg",
    size: 0,
    previewUrl,
    uploadedAt: "",
    source: "s3",
    s3Key,
  };
};

// Backend location RB Pay profile response -> FE setup state. `base` carries the
// location identity and network config (statements URL / contact email) that the
// profile response does not include.
export const deserializeLocationProfile = (
  profile: any,
  base: RBPayrollSetupState
): RBPayrollSetupState => {
  // Status is the source of truth (rbPayStatus), with rbPayApproved as a fallback.
  // `approved` now tracks ACTIVE so the rest of the state stays internally consistent
  // (an On Hold / Pending location is not "approved").
  const status = resolveOwnerProfileStatus(profile);
  const approved = status === RBPayrollStatus.ACTIVE;

  return {
    ...base,
    directDeposit: {
      institutionNumber: String(profile?.institutionNumber || ""),
      transitNumber: String(profile?.transitNumber || ""),
      accountNumber: String(profile?.accountNumber || ""),
      document: buildVoidChequeDocument(profile?.voidChequeS3Key),
      authorizationAccepted: Boolean(profile?.padAuthorized),
    },
    approval: {
      ...base.approval,
      paymentMethod: RBPayrollPaymentMethod.RB_PAYROLL,
      status,
      approved,
      submittedAt: String(profile?.createdAt || base.approval.submittedAt || ""),
      approvedAt: approved ? String(profile?.updatedAt || "") : "",
    },
    meta: {
      updatedAt: String(profile?.updatedAt || profile?.lastUpdatedDate || base.meta.updatedAt || ""),
      source: "be",
    },
  };
};

// FE setup state -> backend create/update payload. Owners submit banking and
// authorization only; approval (`rbPayApproved`) is set by admins, never here.
export const serializeLocationProfile = (setup: RBPayrollSetupState) => {
  const directDeposit = setup.directDeposit;
  const payload: any = {
    institutionNumber: String(directDeposit.institutionNumber || ""),
    transitNumber: String(directDeposit.transitNumber || ""),
    accountNumber: String(directDeposit.accountNumber || ""),
    paymentType: LOCATION_RB_PAY_PAYMENT_TYPE,
    padAuthorized: Boolean(directDeposit.authorizationAccepted),
  };

  // Only forward an existing S3 key; a freshly uploaded document already carries
  // its key from the upload endpoint.
  if (directDeposit.document?.s3Key) {
    payload.voidChequeS3Key = directDeposit.document.s3Key;
  }

  return payload;
};
